/**
 * "Which bed did they go into?" (D69, owner's decision 6; critique R5, R6, R22; tests 34, 38 and 42): one sheet, asked
 * when a Seed vault plant leaves the vault, from a Sow outside or Plant out tick (useTodoMarks) or Record a stage on the
 * plant card (PlantStageLine). It lists the garden's live beds, the vault left out, each a 44 px row; Seeding's
 * question adds "Indoors (stays in the Seed vault)" first. No "new bed" here (R5): with no other bed it says "Make a
 * bed on the Beds page first". Save is the panel's primary, with a Cancel on its left (ActionRow, B111) that answers nothing, as the X does; nothing is ticked.
 *
 * WhichBedHost draws it once, in the layout, for whichever door asked (lib/whichBed askWhichBed). The beds are read
 * with GET /locations under WHICH_BED_LOCATIONS_KEY (under the locations key, so a bed change refreshes it).
 *
 * Words (en/es): todo.whichBed.title, noBeds, indoors, save; sitewide.cancel (ActionRow).
 *
 * @module WhichBedSheet
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BottomPanel, PanelOption } from '@/shared/components/ui/bottom-panel';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { getLocations } from '@/shared/api/locations';
import { answerWhichBed, bedsToOffer, openWhichBed, subscribeWhichBed, type BedAnswer, type BedAsk } from './lib/whichBed';
import { WHICH_BED_LOCATIONS_KEY } from './lib/seedOrderQueries';

/** The "Indoors" choice's value in the sheet. */
const INDOORS = 'indoors';

/**
 * The sheet itself.
 *
 * @param props.ask - The open question, or null when closed.
 * @param props.onAnswer - The answer (null: closed).
 */
export const WhichBedSheet: React.FC<{ ask: BedAsk | null; onAnswer: (answer: BedAnswer) => void }> = ({ ask, onAnswer }) => {
  const { reactTranslate: t } = useTranslation();
  const open = ask !== null;
  const { data: locations = [] } = useQuery({ queryKey: WHICH_BED_LOCATIONS_KEY, queryFn: () => getLocations(1), enabled: open });
  const beds = bedsToOffer(locations);
  const [picked, setPicked] = React.useState<number | typeof INDOORS | null>(null);
  React.useEffect(() => { if (open) setPicked(null); }, [open]);

  /** Save: the bed, or Indoors. */
  const save = (): void => {
    if (picked === null) return;
    onAnswer(picked === INDOORS ? { indoors: true } : { bedId: picked });
  };

  return (
    <BottomPanel open={open} onOpenChange={(next) => { if (!next) onAnswer(null); }} title={t('todo.whichBed.title')} wrapTitle>
      {ask?.sub && <p className="-mt-1 text-sm text-muted-foreground">{ask.sub}</p>}
      <div role="listbox" aria-label={t('todo.whichBed.title')} className="mt-1 max-h-[50dvh] overflow-y-auto px-2">
        {ask?.indoors && (
          <PanelOption selected={picked === INDOORS} onClick={() => setPicked(INDOORS)}>{t('todo.whichBed.indoors')}</PanelOption>
        )}
        {beds.map((bed) => (
          <PanelOption key={bed.id} selected={picked === bed.id} onClick={() => setPicked(bed.id)}>{bed.name}</PanelOption>
        ))}
      </div>
      {beds.length === 0 && <p className="text-sm text-muted-foreground">{t('todo.whichBed.noBeds')}</p>}
      <div className="mt-3">
        <ActionRow onCancel={() => onAnswer(null)}>
          <Button type="button" onClick={save} disabled={picked === null}>{t('todo.whichBed.save')}</Button>
        </ActionRow>
      </div>
    </BottomPanel>
  );
};

/** Draws the sheet for whichever door asked (lib/whichBed); mounted once, in the layout. */
export const WhichBedHost: React.FC = () => {
  const ask = React.useSyncExternalStore(subscribeWhichBed, openWhichBed, openWhichBed);
  return <WhichBedSheet ask={ask} onAnswer={answerWhichBed} />;
};
